import { useLoadingOverlay } from "@/Shared/Hooks/useLoadingOverlay";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { UUID } from "@/Shared/types";
import type { ReactNode } from "react";
import { useShipmentService } from "@/Services/ShipmentService";
import { useSwitchBrokerDialog } from "../Dialogs/ViewShipmentsSwitchBrokerDialog";
import { useUpdatedBOLDialog } from "../Dialogs/ViewShipmentsUpdatedBOLDialog";

export function useViewShipmentsChangeBrokerAfterBookingDialog(
  shipmentId: UUID,
  shipmentState: ShipmentState,
  currentBroker: Broker | undefined,
): { show: () => Promise<boolean>; dialogs: ReactNode } {
  const switchBrokerDialog = useSwitchBrokerDialog();
  const updatedBOLDialog = useUpdatedBOLDialog();
  const shipmentService = useShipmentService();
  const { setLoading, overlay: loadingOverlay } = useLoadingOverlay();

  const dialogs = (
    <>
      {loadingOverlay}
      {switchBrokerDialog.dialogs}
      {updatedBOLDialog.dialogs}
    </>
  );

  // Returns if the broker has changed, hence we should reload
  async function show(): Promise<boolean> {
    if (currentBroker === undefined) {
      return false;
    }

    // We need to show a screen that allows them to select from any of the saved brokers,
    // and optionally modify one of them or add a new one
    const newSavedBrokerId = await switchBrokerDialog.show(currentBroker);

    if (newSavedBrokerId === undefined) {
      // Cancelled
      return false;
    }

    setLoading(true);
    // Make an API call to change the broker
    await shipmentService.setBroker(shipmentId, newSavedBrokerId);
    setLoading(false);

    // If the shipment is in transit, then it's too late to give the driver
    // a new BOL. But if it's still showing as pending pickup, it'd worth showing them this screen
    if (shipmentState !== ShipmentState.InTransit) {
      await updatedBOLDialog.show();
    }

    // Do we need to do something with the custom documents?
    // CS: Jan 2022: I'm currently taking the stance that we do nothing
    // - If they're not yet uploaded then nothing to do anyway
    // - If they have been uploaded, then they're still probably mostly valid, and hopefully
    //   the carrier can work with them
    // We should at some point give the customer the ability to upload replacement customs docs
    return true;
  }

  return { show, dialogs };
}
